/**
 * Catálogo de los 9 estados de envío de Shopify que pueden disparar un aviso.
 *
 * `value` es el valor literal que Shopify pone en `fulfillment.shipment_status`
 * (salvo los derivados, ver más abajo). No renombrar: es la clave con la que se
 * guarda la asignación en `config_values.shipping_events` y la que lee n8n.
 *
 * `risk` marca los estados cuyo significado NO es el que sugiere su nombre y que
 * por tanto necesitan un aviso al usuario antes de activarlos.
 *
 * @typedef  {Object} ShippingEvent
 * @property {string} value  Clave del estado (la de Shopify).
 * @property {string} group  Bloque de la rejilla: preparation | onTheWay | pickup | issues.
 * @property {boolean} risk  Si true, se pinta un badge de advertencia.
 * @property {boolean} recommended  Entra en el juego recomendado de avisos.
 * @property {boolean} [note]  Si true, se muestra una nota informativa (no es una advertencia).
 */

/** @type {ShippingEvent[]} */
export const SHIPPING_EVENTS = [
    { value: 'label_printed', group: 'preparation', risk: false, recommended: false },
    { value: 'label_purchased', group: 'preparation', risk: false, recommended: false },
    { value: 'confirmed', group: 'preparation', risk: true, recommended: false },
    // `shipped` NO es un shipment_status de Shopify: lo derivamos nosotros de
    // "envío creado + con número de seguimiento". Es el único disparo fiable de
    // "ha salido" para las tiendas cuyo transportista Shopify no rastrea, que en
    // la práctica son casi todas fuera de EE. UU.
    { value: 'shipped', group: 'onTheWay', risk: false, recommended: true, note: true },
    { value: 'in_transit', group: 'onTheWay', risk: false, recommended: true },
    { value: 'out_for_delivery', group: 'onTheWay', risk: false, recommended: true },
    { value: 'delivered', group: 'onTheWay', risk: false, recommended: true },
    { value: 'ready_for_pickup', group: 'pickup', risk: false, recommended: false },
    { value: 'attempted_delivery', group: 'issues', risk: false, recommended: false },
    { value: 'failure', group: 'issues', risk: true, recommended: false },
];

/** Orden de los bloques en la rejilla del selector. */
export const SHIPPING_EVENT_GROUPS = ['preparation', 'onTheWay', 'pickup', 'issues'];

/** Umbrales de riesgo de quality rating, por número de avisos que recibe un mismo pedido. */
export const RISK_THRESHOLDS = { medium: 4, high: 6 };

const VALID_EVENTS = new Set(SHIPPING_EVENTS.map((event) => event.value));

/**
 * @param {string} value
 * @returns {boolean} true si es uno de los 9 estados conocidos.
 */
export const isKnownShippingEvent = (value) => VALID_EVENTS.has(value);

/**
 * Construye el nombre de plantilla a partir del nombre que escribe el usuario.
 *
 * No se reutiliza `buildTemplateName()` de ThanksOrder/PlatformRegistry: su prefijo
 * está cableado a `thanks_order` y a los prefijos de reservas.
 *
 * ⚠️ El idioma NO se añade aquí: lo concatena el backend
 * (`TemplateDomainService.java:64-66`). Si lo pusiéramos también nosotros saldría
 * `shipping_update_envios_es_es` — que es exactamente el defecto que arrastra la
 * cuenta Norton con su `ac_es_es`.
 *
 * @param {string} customName Nombre escrito por el usuario.
 * @returns {string}
 */
export const buildShippingTemplateName = (customName) => {
    const clean = (customName || '')
        .toLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/[^a-z0-9]/g, '_')
        .replace(/_+/g, '_')
        .replace(/^_|_$/g, '')
        .substring(0, 30);

    return clean ? `shipping_update_${clean}` : 'shipping_update_default';
};

/**
 * Nombre legible de una plantilla, para la tabla.
 *
 * Misma idea que `parseTemplateName()` de ThanksOrder: quita el prefijo de la
 * convención y los sufijos de idioma finales, que son ruido técnico. Una plantilla
 * ajena a la convención se muestra tal cual, sin tocarla.
 *
 * `shipping_update_envios_es` → «Envios»
 * `flow_shipping_update_1_es` → «flow_shipping_update_1_es» (externa, no es nuestra)
 *
 * @param {string} templateName
 * @returns {string}
 */
export const parseShippingTemplateName = (templateName) => {
    const raw = String(templateName || '').trim();
    if (!raw) return '';
    if (!raw.startsWith('shipping_update_')) return raw;

    const withoutPrefix = raw.slice('shipping_update_'.length);
    const withoutLang = withoutPrefix.replace(/(_[a-z]{2}(_[a-z]{2})?)+$/i, '');
    const base = withoutLang || withoutPrefix;
    if (!base || base === 'default') return raw;

    const pretty = base.replace(/_/g, ' ').trim();
    return pretty.charAt(0).toUpperCase() + pretty.slice(1);
};

/**
 * Detecta plantillas creadas fuera de Lixsa: sin `skillId` o con variables numeradas
 * ({{1}}, {{2}}…) en vez de nombradas.
 *
 * Importa porque `GET template?flowType=SHIPPING_UPDATE` filtra SOLO por flowType:
 * la plantilla `flow_shipping_update_1_es` de Plumens, creada a mano en Meta en enero
 * de 2025 y APROBADA, aparecerá en la lista. Si se reguarda desde el editor, el backend
 * la borra y la recrea en Meta (TemplateDomainService.java:97-100) y se pierde.
 *
 * @param {{skillId?: string, body?: string}} template
 * @returns {boolean}
 */
export const isLegacyPositionalTemplate = (template) => {
    if (!template) return false;
    return !template.skillId || /\{\{\s*\d+\s*\}\}/.test(template.body || '');
};

export default SHIPPING_EVENTS;
